import React from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import Link from 'nextDir/modules/Router/Link';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCountryData } from 'src/common/hooks/useCountryData';
import { ROUTES } from 'src/common/routes';
import GlintsContainer from 'src/components/GlintsContainer';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { SlugTypes } from 'src/modules/Opportunities/utils/slugUrlParams';
import { HierarchicalJobCategory } from 'src/modules/Profile/graphql/hierarchicalJobCategories';
import {
  countrySpecificDescriptionMessages,
  countrySpecificH1Messages,
  countrySpecificTitleMessages,
} from 'src/modules/Seo/careerPageMessages';
import {
  GetActiveJobsArg,
  GetHierarchicalJobCategoriesWithActiveJobsData,
  queryHierarchicalJobCategoriesWithActiveJobs,
} from 'src/modules/Seo/graphql';
import * as S from 'src/modules/Seo/JobsIndexPage.sc';
import { Category, GroupedCategories, SubCategory } from 'src/modules/Seo/type';

const JobsByJobCategoryPage: React.FC<
  React.PropsWithChildren<{
    initialJobCategoriesData: GetHierarchicalJobCategoriesWithActiveJobsData;
  }>
> = ({ initialJobCategoriesData }) => {
  const intl = useIntl();
  const { countryCode, englishCountryName } = useCountryData();
  const time = moment().format('MMMM, YYYY');

  const shouldSkipQuery =
    initialJobCategoriesData?.getHierarchicalJobCategoriesWithActiveJobs
      ?.jobCategories?.length > 0;

  const { data } = useQuery<
    GetHierarchicalJobCategoriesWithActiveJobsData,
    GetActiveJobsArg
  >(queryHierarchicalJobCategoriesWithActiveJobs, {
    variables: {
      countryCode,
      shouldGetAllData: true,
    },
    skip: shouldSkipQuery,
  });

  const browseJobCategoriesData = shouldSkipQuery
    ? initialJobCategoriesData
    : data;

  const groupedData: GroupedCategories = browseJobCategoriesData
    ? groupByCategory(
        browseJobCategoriesData.getHierarchicalJobCategoriesWithActiveJobs
          .jobCategories
      )
    : [];

  const jobCategoriesData = groupedData.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <>
      <MetadataRenderer
        title={intl.formatMessage(countrySpecificTitleMessages[countryCode], {
          country: englishCountryName,
        })}
        description={intl.formatMessage(
          countrySpecificDescriptionMessages[countryCode],
          {
            country: englishCountryName,
            time,
          }
        )}
        glintsTrackingName="explore"
      />

      <GlintsContainer>
        <S.Header>
          <FormattedMessage
            {...countrySpecificH1Messages[countryCode]}
            values={{ country: englishCountryName }}
          />
        </S.Header>

        {jobCategoriesData && jobCategoriesData.length > 0 && (
          <>
            <S.ListContainer>
              {jobCategoriesData.map((category) => {
                return (
                  <S.OuterItem key={category.id}>
                    {/* Category */}
                    <CategoryComponent category={category} />
                    <S.CustomHr />
                    {category.subcategories.map((subcategory, index) => {
                      return (
                        <>
                          <S.MiddleContainer>
                            {/* Subcategory */}
                            <SubcategoryComponent
                              key={subcategory.id}
                              subcategory={subcategory}
                              category={category}
                            />
                            {/* Job Role */}
                            <S.InnerContainer>
                              {subcategory.jobRoles.map((jobRole) => (
                                <JobRoleComponent
                                  key={jobRole.id}
                                  jobRole={jobRole}
                                  subcategory={subcategory}
                                  category={category}
                                />
                              ))}
                            </S.InnerContainer>
                          </S.MiddleContainer>
                          {index !== category.subcategories.length - 1 && (
                            <S.CustomHr isInMiddleContainer={true} />
                          )}
                        </>
                      );
                    })}
                  </S.OuterItem>
                );
              })}
            </S.ListContainer>
          </>
        )}
      </GlintsContainer>
    </>
  );
};

function groupByCategory(categories: any[]): GroupedCategories {
  if (!categories || (Array.isArray(categories) && categories.length === 0)) {
    return [];
  }

  function createSubcategoryObject(
    subcategory: HierarchicalJobCategory,
    category: HierarchicalJobCategory
  ): SubCategory {
    return {
      id: subcategory.id,
      name: subcategory.name,
      slug: subcategory.slug,
      jobRoles: [category],
    };
  }

  return categories.reduce(
    (
      groupedCategories: GroupedCategories,
      currCategory: HierarchicalJobCategory
    ) => {
      const category = currCategory.parents.find(
        (parent: any) => parent.level === 1
      );
      const subcategory = currCategory.parents.find(
        (parent: any) => parent.level === 2
      );

      if (!category || !subcategory) {
        return groupedCategories;
      }

      const existingCategory = groupedCategories.find(
        (e: any) => e.id === category.id
      );

      if (!existingCategory) {
        const categoryDetails: Category = {
          id: category.id,
          name: category.name,
          slug: category.slug,
          subcategories: [createSubcategoryObject(subcategory, currCategory)],
        };
        return [...groupedCategories, categoryDetails];
      }

      const existingSubcategory = existingCategory.subcategories.find(
        (c: any) => c.id === subcategory.id
      );

      if (!existingSubcategory) {
        const newSubcategory = createSubcategoryObject(
          subcategory,
          currCategory
        );
        existingCategory.subcategories.push(newSubcategory);
      } else {
        existingSubcategory.jobRoles.push(currCategory);
      }

      return groupedCategories;
    },
    []
  );
}

/** Components **/
const CategoryComponent = ({ category }: { category: Category }) => {
  return (
    <Link
      href={`/${ROUTES.opportunitiesJobsExplore}`}
      as={`/${SlugTypes.JOB_CATEGORY}/${category.slug}`}
    >
      {category.name}
    </Link>
  );
};

const SubcategoryComponent = ({
  subcategory,
  category,
}: {
  subcategory: SubCategory;
  category: Category;
}) => {
  return (
    <S.MiddleItem>
      <Link
        href={`/${ROUTES.opportunitiesJobsExplore}`}
        as={`/${SlugTypes.JOB_CATEGORY}/${category.slug}/${subcategory.slug}`}
      >
        {subcategory.name}
      </Link>
    </S.MiddleItem>
  );
};

const JobRoleComponent = ({
  jobRole,
  subcategory,
  category,
}: {
  jobRole: HierarchicalJobCategory;
  subcategory: SubCategory;
  category: Category;
}) => {
  return (
    <>
      <li>
        <Link
          key={jobRole.id}
          href={`/${ROUTES.opportunitiesJobsExplore}`}
          as={`/${SlugTypes.JOB_CATEGORY}/${category.slug}/${subcategory.slug}/${jobRole.slug}`}
        >
          <h4>{jobRole.name}</h4>
        </Link>
      </li>
    </>
  );
};

export default React.memo(JobsByJobCategoryPage);
